/* eslint-disable import/prefer-default-export */
import { PayloadAction } from '@reduxjs/toolkit';
import { AxiosError, AxiosResponse } from 'axios';
import { call, put, takeLatest } from 'redux-saga/effects';

import {
  getVodPost,
  setVodPost,
  getAllChannel,
  setAllChannel,
  postChannelPostSuccess,
  postChannelPost,
  deleteChannelPostSuccess,
  deleteChannelPost,
  createChannel,
  createChannelSuccess,
  updateChannels,
  updateChannelsSuccess,
  failure,
  unpinAllChannelPost,
  resequenceChannelPosts,
  resequenceChannelPostsSuccess,
} from '@/Redux/slices/VodSlice';

import { fetchVodAPI } from '@/Axios/api/postManagement';
import { PostManagementParam } from '@/Interfaces/I_postManagement';
import {
  createChannelAPI,
  deleteChannelPostAPI,
  fetchAllChannelAPI,
  postChannelPostAPI,
  unpinAllChannelPostApi,
  updateChannelAPI,
  resequenceChannelPostsAPI,
} from '@/Axios/api/channel';
import { message } from 'antd';
import { ChannelData, ChannelPostDataParam, CreateChannelRequest, ResequenceChannelPostsParam } from '@/Interfaces/I_vod';

// Lite App
import {
  LiteAppPostData,
  LiteAppPostDataParam,
  UpdateLiteAppPostRequest,
} from '@/Interfaces/I_LiteApp';
import {
  createLiteAppPostAPI,
  deleteLiteAppPostAPI,
  fetchAllLiteAppPostAPI,
  updateLiteAppPostAPI,
} from '@/Axios/api/liteApp';
import { VersionData } from '@/Interfaces/common';
import {
  getLiteAppPosts,
  setLiteAppPosts,
  createLiteAppPost,
  createLiteAppPostSuccess,
  deleteLiteAppPost,
  deleteLiteAppPostSuccess,
  updateLiteAppPosts,
  updateLiteAppPostsSuccess,
  liteAppFailure,
} from '../slices/LiteAppSlice';

function* handleVodGetPost(action: PayloadAction<PostManagementParam>) {
  try {
    const data: AxiosResponse = yield call(fetchVodAPI, action.payload.params);
    yield put(setVodPost(data));
  } catch (e) {
    message.error({
      content: 'Fail to get vod post, please try again.',
      duration: 3,
      style: {
        marginTop: '120px',
      },
    });
  }
}

function* handleGetAllChannel(_action: PayloadAction) {
  try {
    const data: AxiosResponse<VersionData<ChannelData[]>> = yield call(fetchAllChannelAPI);
    yield put(setAllChannel(data.data));
  } catch (e) {
    message.error({
      content: 'Fail to get channels, please try again.',
      duration: 3,
      style: {
        marginTop: '120px',
      },
    });
  }
}

function* handlePostChannelPost(action: PayloadAction<ChannelPostDataParam>) {
  try {
    yield call(postChannelPostAPI, action.payload);
    yield put(postChannelPostSuccess(action.payload));
    yield put(getAllChannel());
  } catch (e) {
    message.error({
      content: 'Fail to add post in channel, please try again.',
      duration: 3,
      style: {
        marginTop: '120px',
      },
    });
  }
}

function* handleDeleteChannelPost(action: PayloadAction<ChannelPostDataParam>) {
  try {
    yield call(deleteChannelPostAPI, action.payload);
    yield put(deleteChannelPostSuccess(action.payload));
    yield put(getAllChannel());
  } catch (e) {
    message.error({
      content: 'Fail to add post in channel, please try again.',
      duration: 3,
      style: {
        marginTop: '120px',
      },
    });
  }
}

function* handleCreateChannel(action: PayloadAction<VersionData<CreateChannelRequest>>) {
  try {
    const data: AxiosResponse<VersionData<ChannelData[]>> = yield call(
      createChannelAPI,
      action.payload,
    );
    yield put(createChannelSuccess(data.data));
  } catch (e) {
    const error = e as AxiosError;
    message.error({
      content: error.response?.data.message || 'Fail to create channel, please try again.',
      duration: 3,
      style: {
        marginTop: '120px',
      },
    });
    yield put(failure({}));
  }
}

function* handleUpdateChannels(action: PayloadAction<VersionData<ChannelData[]>>) {
  try {
    const data: AxiosResponse<VersionData<ChannelData[]>> = yield call(
      updateChannelAPI,
      action.payload,
    );
    yield put(updateChannelsSuccess(data.data));
  } catch (e) {
    const error = e as AxiosError;
    message.error({
      content: error.response?.data.message || 'Fail to updated channels, please try again.',
      duration: 3,
      style: {
        marginTop: '120px',
      },
    });
    yield put(failure({}));
  }
}

function* handleUnpinAllChannelPost(action: PayloadAction<Number[]>) {
  try {
    const data: AxiosResponse = yield call(
      unpinAllChannelPostApi,
      action.payload
    );
    message.success({
      content: 'Unpin Channels success',
      duration: 3,
      style: {
        marginTop: '120px',
      },
    })
  } catch (e) {
    const error = e as AxiosError;
    message.error({
      content: error.response?.data.message || 'Fail to unpin channels, please try again.',
      duration: 3,
      style: {
        marginTop: '120px',
      },
    });
    yield put(failure({}));
  }
}

function* handleResequenceChannelPosts(action: PayloadAction<ResequenceChannelPostsParam>) {
  try {
    const deduped = [...new Set(action.payload.postOrder)];
    const param: ResequenceChannelPostsParam = { channelId: action.payload.channelId, postOrder: deduped };
    yield call(resequenceChannelPostsAPI, param);
    yield put(resequenceChannelPostsSuccess());
    yield put(getAllChannel());
  } catch (e) {
    const error = e as AxiosError;
    message.error({
      content: error.response?.data.message || 'Fail to resequence channel posts, please try again.',
      duration: 3,
      style: {
        marginTop: '120px',
      },
    });
    yield put(failure({}));
  }
}

function* handleGetLiteAppPost() {
  try {
    const data: AxiosResponse<VersionData<LiteAppPostData[]>> = yield call(fetchAllLiteAppPostAPI);
    yield put(setLiteAppPosts(data.data));
  } catch (e) {
    message.error({
      content: 'Fail to get lite app post, please try again.',
      duration: 3,
      style: {
        marginTop: '120px',
      },
    });
  }
}

function* handleCreateLiteAppPost(action: PayloadAction<VersionData<LiteAppPostDataParam>>) {
  try {
    const data: AxiosResponse<VersionData<LiteAppPostData[]>> = yield call(
      createLiteAppPostAPI,
      action.payload,
    );
    yield put(createLiteAppPostSuccess(data.data));
  } catch (e) {
    message.error({
      content:
        (e as AxiosError)?.response?.data?.message ||
        'Fail to create post in lite app, please try again.',
      duration: 3,
      style: {
        marginTop: '120px',
      },
    });
    yield put(liteAppFailure({}));
  }
}

function* handleUpdateLiteAppPost(action: PayloadAction<VersionData<UpdateLiteAppPostRequest>>) {
  try {
    const data: AxiosResponse<VersionData<LiteAppPostData[]>> = yield call(
      updateLiteAppPostAPI,
      action.payload,
    );
    yield put(updateLiteAppPostsSuccess(data.data));
  } catch (e) {
    message.error({
      content:
        (e as AxiosError)?.response?.data?.message ||
        'Fail to update post in lite app, please try again.',
      duration: 3,
      style: {
        marginTop: '120px',
      },
    });
    yield put(liteAppFailure({}));
  }
}

function* handleDeleteLiteAppPost(action: PayloadAction<VersionData<string>>) {
  try {
    const data: AxiosResponse<VersionData<LiteAppPostData[]>> = yield call(
      deleteLiteAppPostAPI,
      action.payload,
    );
    yield put(deleteLiteAppPostSuccess(data.data));
  } catch (e) {
    message.error({
      content:
        (e as AxiosError)?.response?.data?.message ||
        'Fail to delete post in lite app, please try again.',
      duration: 3,
      style: {
        marginTop: '120px',
      },
    });
    yield put(liteAppFailure({}));
  }
}

export function* watchGetPost() {
  yield takeLatest(getVodPost.type, handleVodGetPost);
  yield takeLatest(getAllChannel.type, handleGetAllChannel);
  yield takeLatest(postChannelPost.type, handlePostChannelPost);
  yield takeLatest(deleteChannelPost.type, handleDeleteChannelPost);
  yield takeLatest(createChannel.type, handleCreateChannel);
  yield takeLatest(updateChannels.type, handleUpdateChannels);
  yield takeLatest(getLiteAppPosts.type, handleGetLiteAppPost);
  yield takeLatest(createLiteAppPost.type, handleCreateLiteAppPost);
  yield takeLatest(updateLiteAppPosts.type, handleUpdateLiteAppPost);
  yield takeLatest(deleteLiteAppPost.type, handleDeleteLiteAppPost);
  yield takeLatest(unpinAllChannelPost.type, handleUnpinAllChannelPost);
  yield takeLatest(resequenceChannelPosts.type, handleResequenceChannelPosts);
}
